import {NEW_ID} from '../../../../common/constants/index.js'
import Select from '../../Common/Select.js'
import Checkbox from '../../Common/Checkbox.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import ButtonSecondary from '../../../../common/components/Button/ButtonSecondary.js'

import Archive from './Archive.js'
import Nickname from './Nickname.js'
import ShippingMethods from './ShippingMethods.js'
import ShipperOptions from '../../../../data/shipperOptions.js'
import {
  formSelector,
  getState,
  onlyIfAutoForm,
  updateForm,
  useSelector,
} from '../../../../store.js'
import api from '../../../../common/api.js'
import {FEDEX} from '../../../../common/constants/ShipperNames.js'
import {showGlobalError} from '../../../GlobalErrorMessage.js'
import {setShipper} from '../../../../data/shippers.js'
import {isPresent} from '../../../../common/utils.js'
import {showFedExAuthModal} from '../FedExAuthModal.js'
import ShipperImage from './ShipperImage.js'
import {
  baseSaveParamsSelector,
  baseShipperFormErrorsSelector,
  baseShipperInitialFormSelector,
  goToShipperSettings,
} from '../ShippersFunctions.js'

export function setUpShipperForm({shipper, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {shipper}),
      errorsSelector,
    },
  }
}

export function errorsSelector(state, {formName}) {
  const errors = baseShipperFormErrorsSelector(state, {formName})
  const form = formSelector(state, {
    formName,
  })

  if (!isPresent(form.accountNumber)) {
    errors.accountNumber = 'Account number is required'
    errors.preventSave = true
  }

  return errors
}

export function initialFormSelector(state, {shipper}) {
  const vendorConfig = shipper?.vendor_config || {}

  return {
    ...baseShipperInitialFormSelector(state, {shipper, vendor: FEDEX}),
    accountNumber: vendorConfig.account_number || '',
    smartPostHub: vendorConfig.smartpost_hub || '',
    use_metric_units: vendorConfig.use_metric_units || false,
    has_priority_alert: vendorConfig.has_priority_alert || false,
    letterhead_image: vendorConfig.letterhead_image || false,
    signature_image: vendorConfig.signature_image || false,
  }
}

export async function saveShipperFedEx(formName) {
  const form = formSelector(getState(), {formName})
  let [shipperID, params] = baseSaveParamsSelector(getState(), {formName})

  const {smartPostHub, use_metric_units, has_priority_alert} = form

  params = {
    ...params,
    smartpost_hub: smartPostHub,
    use_metric_units,
    has_priority_alert,
  }

  updateForm(formName, {isSaving: true})

  try {
    const {json} = await (shipperID !== NEW_ID
      ? api.put(`/shipper/${shipperID}/`, params)
      : api.post('/shipper/', {...params, vendor: FEDEX}))

    goToShipperSettings()

    setShipper(json)
  } catch (err) {
    showGlobalError(
      {
        summary: 'There was an error while saving shipper.',
      },
      err,
    )

    updateForm(formName, {isSaving: false})
  }
}

function FedExForm({formName, form}) {
  const {
    id,
    accountNumber,
    smartPostHub,
    use_metric_units,
    has_priority_alert,
    letterhead_image,
    signature_image,
    isSaving,
  } = form

  const errors = useSelector((state) => form.errorsSelector(state, {formName}))

  return (
    <div className="settings-list-item-form-wrap clear-both">
      <div className="medium-7 columns">
        {id !== NEW_ID && (
          <fieldset>
            <ul className="form-list margin-bottom-0">
              {id !== NEW_ID && <Nickname formName={formName} />}
              <li className="form-list-item fs-00 margin-top-10">
                <strong>Account Number:</strong> {accountNumber}
              </li>
              <li className="form-list-item fs-00 margin-top-5 padding-bottom-15 divider--bottom">
                <ButtonPrimary
                  alt
                  size="sm"
                  onClick={() =>
                    showFedExAuthModal(id === NEW_ID ? undefined : id)
                  }
                >
                  {id === NEW_ID ? 'Create' : 'Reauthorize'}
                </ButtonPrimary>
              </li>
              <li className="form-list-item bigger margin-bottom-20">
                <Select
                  label="Default Hub"
                  value={smartPostHub}
                  onChange={(event) =>
                    updateForm(formName, {smartPostHub: event.target.value})
                  }
                >
                  <option value="">No Default</option>
                  {ShipperOptions.fedex.hub.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.display}
                    </option>
                  ))}
                </Select>
              </li>
              <ShipperImage
                shipperID={id}
                imageType="letterhead_image"
                label="Letterhead"
                maxDimensions={[700, 50]}
                hasExistingImage={letterhead_image}
              />
              <ShipperImage
                shipperID={id}
                imageType="signature_image"
                label="Signature"
                maxDimensions={[240, 25]}
                hasExistingImage={signature_image}
              />
              <li className="form-list-item bigger margin-bottom-20 divider--top">
                <Checkbox
                  label="Has FedEx Priority Alert® Enabled"
                  id="has_priority_alert"
                  checked={has_priority_alert}
                  onChange={() =>
                    updateForm(formName, {
                      has_priority_alert: !has_priority_alert,
                    })
                  }
                />
              </li>
              {id !== NEW_ID && <ShippingMethods formName={formName} />}
              <li className="form-list-item bigger margin-top-20 margin-bottom-30 divider--top">
                <dl className="list margin-bottom-15">
                  <dt className="list__title fs-00">
                    Do you want to convert to metric units when creating labels?
                  </dt>
                  <dd className="list__item fs-n0">
                    In Ordoro, weight and dimensions are displayed (and stored)
                    in imperial units. We can, however, convert weights and
                    dimensions to metric units while requesting rates and labels
                    from FedEx. Selecting the following checkbox will make this
                    conversion happen behind-the-scenes (units will still be
                    displayed in imperial).
                  </dd>
                </dl>
                <Checkbox
                  label="Convert to metric units"
                  id="use_metric_units"
                  checked={use_metric_units}
                  onChange={() =>
                    updateForm(formName, {use_metric_units: !use_metric_units})
                  }
                />
              </li>
              <li className="list__item list__item--no-style">
                <ButtonPrimary
                  isLoading={isSaving}
                  isDisabled={errors.preventSave}
                  onClick={() => saveShipperFedEx(formName)}
                >
                  {id !== NEW_ID ? 'Save' : 'Create'}
                </ButtonPrimary>
                <ButtonSecondary onClick={() => goToShipperSettings()}>
                  Cancel
                </ButtonSecondary>
              </li>
            </ul>
          </fieldset>
        )}
        {id == NEW_ID && (
          <li className="form-list-item fs-00 margin-top-5">
            <ButtonPrimary
              onClick={() => showFedExAuthModal(id === NEW_ID ? undefined : id)}
            >
              Add a FedEx Account
            </ButtonPrimary>
          </li>
        )}
      </div>

      <div className="medium-5 columns">
        <div className="alert margin-bottom-10 alert--standard">
          {id === NEW_ID ? (
            <dl className="list">
              <dt className="list__title fs-00 lh-md margin-bottom-5">
                Need help authorizing your FedEx account?
              </dt>
              <dd className="list__item fs-n0 margin-bottom-0">
                <a
                  className="btn--link mid meta-ups-setup-help"
                  href="https://support.ordoro.com/fedex-setup/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get step-by-step instructions →
                </a>
              </dd>
            </dl>
          ) : (
            <Archive formName={formName} />
          )}
        </div>
        <p className="fs-n1 lh-md op-75">
          FedEx service marks are owned by Federal Express Corporation and are
          used by permission.
        </p>
      </div>
    </div>
  )
}

export default onlyIfAutoForm(FedExForm, setUpShipperForm)
